// S7 cost-run helpers — PURE functions, no React/network, so the cost-sensitive
// arithmetic (old→new delta) is exhaustively unit-testable (Module 01 §3.7, §4).
//
// Redaction contract (§2.16, §7): the `*_minor` cost fields are omitted by the
// server for a role without catalogue.cost.read, so every number that flows in
// here is `number | null | undefined`:
//   undefined → redacted (no access)      → the cost column is hidden entirely.
//   null      → present but not costed yet → rendered as an em dash / "no baseline".
//   number    → an integer minor-unit cost → formatted / used in the delta.
// These helpers never assume a cost is present.

import { shortId as shortIdTail } from "../../../../lib/short-id";
import type { components } from "@assessly/api-client";

type CostItem = components["schemas"]["CostItem"];
type CostRun = components["schemas"]["CostRun"];
type Product = components["schemas"]["Product"];

/** A cost basis: an integer minor-unit amount priced per `priceUnitQty` units. */
export interface CostBasis {
  minor: number | null | undefined;
  priceUnitQty: number;
}

/**
 * Per-unit cost = minor ÷ price_unit_qty. Returns undefined when the amount is
 * absent (redacted / uncosted) or the basis is non-positive, so callers never
 * divide by zero or fabricate a cost the server withheld.
 */
export function perUnitMinor(
  minor: number | null | undefined,
  priceUnitQty: number,
): number | undefined {
  if (minor === null || minor === undefined) return undefined;
  if (!(priceUnitQty > 0)) return undefined;
  return minor / priceUnitQty;
}

/**
 * The result of comparing a product's current released cost (old) with the cost
 * this run computed (new), normalised to per-unit so a `price_unit_qty` change
 * (e.g. the §3.7 auto-escalation 1→1000) never distorts the percentage.
 *
 *   "redacted"  → the new cost was withheld (no cost.read) → hide the Δ% cell.
 *   "no-baseline" → no prior released cost (a brand-new product) → render "New".
 *   { pct }     → a finite percentage change.
 */
export type DeltaResult =
  { kind: "redacted" } | { kind: "no-baseline" } | { kind: "value"; pct: number };

export function computeDelta(oldCost: CostBasis, newCost: CostBasis): DeltaResult {
  const newPerUnit = perUnitMinor(newCost.minor, newCost.priceUnitQty);
  // A missing NEW cost means the row itself is redacted — nothing to compare.
  if (newPerUnit === undefined) return { kind: "redacted" };
  const oldPerUnit = perUnitMinor(oldCost.minor, oldCost.priceUnitQty);
  if (oldPerUnit === undefined || oldPerUnit === 0) return { kind: "no-baseline" };
  return { kind: "value", pct: ((newPerUnit - oldPerUnit) / oldPerUnit) * 100 };
}

/** Signed, 1-dp percentage string, e.g. `+4.2%`, `-1.3%`, `0.0%`. */
export function formatDeltaPct(pct: number): string {
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

/**
 * Whether a row passes the Δ% filter. Rows with no baseline (new products) are
 * always kept — they are the most significant change. Redacted rows are kept
 * (the column is hidden anyway). A finite delta passes when |Δ%| ≥ threshold.
 */
export function passesMinDelta(delta: DeltaResult, minPct: number | undefined): boolean {
  if (minPct === undefined) return true;
  if (delta.kind !== "value") return true;
  return Math.abs(delta.pct) >= minPct;
}

/**
 * The count for the release confirm summary ("N products will be re-valued").
 * A release re-values every item the run produced, so the honest count is the
 * total number of loaded cost items — not the filtered view.
 */
export function releaseCount(items: readonly CostItem[]): number {
  return items.length;
}

/** True once a run has reached a state where releasing is meaningful. */
export function isReleasable(run: Pick<CostRun, "status">): boolean {
  return run.status === "computed";
}

/** True while a run can still be discarded (i.e. not already terminal). */
export function isDiscardable(run: Pick<CostRun, "status">): boolean {
  return run.status !== "released" && run.status !== "discarded";
}

/** True while a run is still producing items (show the computing hint + skeleton). */
export function isComputing(run: Pick<CostRun, "status">): boolean {
  return run.status === "draft" || run.status === "computing";
}

/** Index products by id for the old-cost / SKU / name join on the detail table. */
export function indexProducts(products: readonly Product[]): Map<string, Product> {
  const byId = new Map<string, Product>();
  for (const product of products) byId.set(product.id, product);
  return byId;
}

/** A short, human-scannable fallback label when a product row is not in the map. */
export function shortId(id: string): string {
  return shortIdTail(id);
}
